import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { User, X, Search, Plus } from 'lucide-react';

export function ClientSearchInput({ clients, value, onChange, onCreateNew, placeholder = "Начните вводить имя клиента...", ...props }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);

  const selectedClient = clients.find(c => c.id === value);

  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const filtered = clients.filter(client =>
        client.name?.toLowerCase().includes(query) ||
        client.phone?.toLowerCase().includes(query) ||
        client.email?.toLowerCase().includes(query) ||
        client.address_string?.toLowerCase().includes(query)
      ).slice(0, 10);
      setFilteredClients(filtered);
    } else {
      setFilteredClients(clients.slice(0, 10));
    }
  }, [searchQuery, clients]);

  const handleSelect = (client) => {
    onChange(client.id, client);
    setSearchQuery('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('', null);
    setSearchQuery('');
  };

  return (
    <div className="relative">
      {selectedClient ? (
        <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/50">
          <User className="h-4 w-4 text-muted-foreground" />
          <span className="flex-1 text-sm">
            {selectedClient.name}
            {selectedClient.phone && <span className="text-muted-foreground ml-2">({selectedClient.phone})</span>}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={handleClear}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onBlur={(e) => {
              setTimeout(() => setIsOpen(false), 200);
            }}
            placeholder={placeholder}
            className="pl-9"
            {...props}
          />
        </div>
      )}

      {isOpen && !selectedClient && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
          {onCreateNew && (
            <button
              type="button"
              className="w-full px-3 py-2 text-left hover:bg-accent flex items-center gap-2 border-b text-primary font-medium"
              onClick={() => { setIsOpen(false); onCreateNew(); }}
            >
              <Plus className="h-4 w-4 shrink-0" />
              <span className="text-sm">Создать клиента</span>
            </button>
          )}
          {filteredClients.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground text-center">
              {searchQuery ? 'Клиенты не найдены' : 'Нет клиентов'}
            </div>
          ) : (
            filteredClients.map(client => (
              <button
                key={client.id}
                type="button"
                className="w-full px-3 py-2 text-left hover:bg-accent flex items-start gap-2 border-b last:border-0"
                onClick={() => handleSelect(client)}
              >
                <User className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{client.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {client.phone && <span>{client.phone}</span>}
                    {client.phone && client.address_string && ' • '}
                    {client.address_string && <span>{client.address_string}</span>}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
